import ArrowLeftOutlinedIcon from '@mui/icons-material/ArrowLeftOutlined';
import LoadingButton from '@mui/lab/LoadingButton';
import { AlertColor, Box, Grid, Typography } from '@mui/material';
import { useContext, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import { resetPasswordWithToken } from '../../../../api/api';
import InputTextController from '../../../../components/Atoms/InputTextController';
import { SimpleAlert } from '../../../../components/Atoms/SimpleAlert';
import DevelopedBySection from '../../../../components/LayoutAuth/DevelopedBySection';
import HeaderLogo from '../../../../components/LayoutAuth/HeaderLogo';
import HelpSection from '../../../../components/LayoutAuth/HelpSection';
import { LOGIN } from '../../../../constants/routes';
import BusinessUnitParamsContext from '../../../../contexts/BusinessUnitParamsContext/businessUnitParamsContext';
import { useStyles } from '../../../../styles/global/stylesLogin';
import { setEmailToResetPassword } from '../../../../util/LocalStorage/resetPassword';

type FormInputs = {
  email: string;
};

const ResetPasswordForm = () => {
  const { businessUnitUUID, contextUrl } = useContext(BusinessUnitParamsContext);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const classes = useStyles();
  const history = useHistory();

  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState<AlertColor>('success');
  const [alertMessage, setAlertMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const { control, handleSubmit } = useForm<FormInputs>({
    mode: 'all',
  });

  const onSubmit = async (data: FormInputs) => {
    setLoading(true);
    setShowAlert(false);
    if (!executeRecaptcha) {
      setLoading(false);
      return console.info('Execute recaptcha not yet available');
    }

    if (data.email != '') {
      const captcha = await executeRecaptcha('submit');

      try {
        const response = await resetPasswordWithToken(
          captcha,
          businessUnitUUID,
          data.email,
          contextUrl
        );
        if (response && response.expiresInMinutes) {
          setLoading(false);
          setShowAlert(true);
          setAlertType('success');
          setAlertMessage('Se envió un enlace de recuperación al correo electrónico ingresado.');
          setEmailToResetPassword(data.email);
        } else {
          setLoading(false);
          setShowAlert(true);
          setAlertType('error');
          setAlertMessage('No hay ningún usuario asociado con ese correo electrónico.');
        }
      } catch (error: any) {
        setLoading(false);
        setShowAlert(true);
        setAlertType('error');
        setAlertMessage(error.response.data.message);
        console.error(error);
      }
    }
  };

  const onError = () => {
    console.error('onError');
  };

  const goToLogin = () => {
    history.push({ pathname: LOGIN });
  };

  return (
    <>
      <HeaderLogo />

      <Grid className={classes.containerTitle}>
        <Grid className={classes.containerIcon}>
          <ArrowLeftOutlinedIcon className={classes.icon} onClick={goToLogin} />
          <Typography className={classes.title}>{'Recuperar contraseña'}</Typography>
        </Grid>
        <Typography className={classes.description}>
          {'Ingrese la dirección de correo electrónico que está asociada a su cuenta'}
        </Typography>
      </Grid>

      <Grid className={classes.containerForm}>
        <Box component="form" onSubmit={handleSubmit(onSubmit, onError)} autoComplete="off">
          <Grid container rowSpacing={2} columnSpacing={5}>
            <Grid item xs={12}>
              <InputTextController
                name={'email'}
                label={'Correo electrónico'}
                placeholder={'Correo electrónico'}
                control={control}
                type={'email'}
                minLength={1}
                required={true}
              />
            </Grid>
            <Grid item xs={12}>
              <LoadingButton
                loading={loading}
                disabled={loading}
                className={loading ? classes.buttonDisabled : classes.button}
                variant="contained"
                size="medium"
                type="submit"
                style={{ textTransform: 'none' }}
                onSubmit={handleSubmit(onSubmit, onError)}
              >
                {'Recuperar contraseña'}
              </LoadingButton>
            </Grid>
          </Grid>
        </Box>

        {showAlert && (
          <Grid className={classes.containerAlert}>
            <SimpleAlert fullWidth text={alertMessage} type={alertType} />
          </Grid>
        )}
      </Grid>

      <Grid className={classes.containerForm}>
        <HelpSection />
      </Grid>

      <DevelopedBySection />
    </>
  );
};

export default ResetPasswordForm;
